/**
 * A charge is a single synchronous processor call, so a pending charge
 * operation older than this is a crashed attempt rather than one in flight.
 * Shared with the admin UI so both sides agree on when a retry is offered.
 */
export const CHARGE_IN_FLIGHT_TIMEOUT_MINUTES = 10;

/**
 * Off-session pay cannot complete 3DS/SCA. Staff must finish the payment on
 * the hosted invoice instead of submitting another processor pay call.
 */
export const CHARGE_AUTHENTICATION_REQUIRED_MESSAGE =
  "Customer authentication is required. Complete payment on the hosted invoice.";

/**
 * The invoice stays paid; the label tells whether the whole payment came back.
 */
export function describeRefund(invoice: {
  amountRefunded: string | null;
  amountPaid: string | null;
  totalAmount: string | null;
}): string | null {
  if (invoice.amountRefunded === null) {
    return null;
  }
  const refunded = Number(invoice.amountRefunded);
  if (refunded <= 0) {
    return null;
  }
  const paid = Number(invoice.amountPaid ?? invoice.totalAmount ?? "0");
  if (refunded >= paid) {
    return "Refunded in full";
  }
  return `Partially refunded ($${invoice.amountRefunded})`;
}

export function describePaidWith(invoice: {
  paidPaymentMethodLabel: string | null;
  paidPaymentMethodLast4: string | null;
}): string | null {
  if (!invoice.paidPaymentMethodLabel) {
    return null;
  }
  return `${invoice.paidPaymentMethodLabel} •••• ${invoice.paidPaymentMethodLast4 ?? "????"}`;
}

/**
 * A direct charge has no hosted invoice to finish 3DS/SCA on, so staff issue
 * it on terms instead, or retry once the unit saves a method that needs none.
 */
export const DIRECT_CHARGE_AUTHENTICATION_REQUIRED_MESSAGE =
  "Customer authentication is required. Retry after the unit updates its saved card, or email the invoice so the customer can pay online.";

/**
 * Each collection path records a 3DS/SCA stop with its own message; callers
 * that only need to know the customer must authenticate accept either.
 */
export function isAuthenticationRequiredMessage(
  message: string | null | undefined,
): boolean {
  return (
    message === CHARGE_AUTHENTICATION_REQUIRED_MESSAGE ||
    message === DIRECT_CHARGE_AUTHENTICATION_REQUIRED_MESSAGE
  );
}
